import { makeAutoObservable } from "mobx";
import ApiStore from "./ApiStore";
import ApplicationStore from "./ApplicationStore";
import { UploadFile } from "antd";
import { InviteUser } from "../pages/Users/AddNewUser";
import { Roles } from "../enums/Roles";
import { JobTitle } from "../models/JobTitle";
import { HistoryItem } from "../models/HistoryItem";
import { Skill } from "../models/Skill";
import { Employee } from "../models/Employee";
import { Experience } from "../models/Experience";

class EmployeesStore {
  isLoading: boolean = true;
  ApiStore!: ApiStore;
  ApplicationStore!: ApplicationStore;
  user: Employee;
  users!: Employee[];
  allUsers!: Employee[];
  roles!: any[];
  jobTitles!: JobTitle[];

  constructor(apiStore: ApiStore, applicationStore: ApplicationStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
    this.ApplicationStore = applicationStore;
    this.user = {} as Employee;
    this.user.userSkills = [];
    this.user.userProjects = [];
    this.users = [];
    this.allUsers = [];
    this.roles = [];
    this.jobTitles = [];
  }

  //#region Loading
  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  }
  //#endregion


  //#region Email
  checkEmail = async (email: string) => {
    return await this.ApiStore.EmployeesApiClient.EmailCheck(email)
  };
  //#endregion


  //#region Invite User
  inviteUser = async (user: InviteUser): Promise<boolean> => {
    const roleIds = user.roles.map(r => Number(Roles[r]));

    const payload = {
      userEmail: user.email,
      userRoles: roleIds,
      chiefId: user.chiefId,
      firstName: user.firstName,
      lastName: user.lastName,
      personalEmails: user.personalEmails,
      jobTitleId: user.jobTitleId,
    };

    return await this.ApiStore.EmployeesApiClient.inviteUser(payload);
  };

  resendInvite = async (
    email: string,
    roleName: keyof typeof Roles,
    chiefId: string,
    jobTitleId: string,
    firstName = '',
    lastName = '',
    personalEmails: string[] = []
  ): Promise<boolean> => {
    if (!(roleName in Roles)) throw new Error('Invalid role name');

    return await this.inviteUser({
      email,
      firstName,
      lastName,
      personalEmails,
      roles: [roleName],
      chiefId,
      jobTitleId
    });
  };
  //#endregion


  //#region Skills
  addSkill = (skill: Skill) => {
    // this.user.userSkills.push(skill);
  }

  removeSkill = (skillId: string) => {
    // this.user.skills = this.user.skills.filter(skill => skill.skillId !== skillId);
  }
  //#endregion


  //#region Job Title
  addJobTitle = async (title: string, description: string) => {
    this.isLoading = true;
    return await this.ApiStore.EmployeesApiClient.addJobTitle(
      title,
      description
    ).then(res => {
      return res;
    }).finally(() => {
      this.isLoading = false;
    });
  }

  getJobTitles = async () => {
    this.isLoading = true;
    const result = await this.ApiStore.EmployeesApiClient.getJobTitles().then(res => {
      this.jobTitles = res;
    }).finally(() => {
      this.isLoading = false;
    });
  }
  //#endregion


  //#region Users
  setUser = (user: Employee) => {
    this.user = user;
  };

  getUsers = async () => {
    this.isLoading = true;
    await this.getJobTitles();
    const users = await this.ApiStore.EmployeesApiClient.getUsers().then(result => {
      const onlyActive = result.filter(el => el.isActive !== false);
      this.allUsers = result;
      this.users = onlyActive.filter(el => el.isApproveInvite != false);
      return onlyActive;
    }).finally(() => {
      this.isLoading = false;
    });
    return users;
  }

  getAllUsers = async () => {
    this.isLoading = true;
    await this.getJobTitles();
    const users = await this.ApiStore.EmployeesApiClient.getUsers()
      .then(result => {
        return result.filter(u => u.isApproveInvite !== false);
      })
      .finally(() => {
        this.isLoading = false;
      });

    return users;
  }

  getUserById = async (id: string): Promise<Employee> => {
    this.isLoading = true;
    await this.getJobTitles();

    try {
      const result = await this.ApiStore.EmployeesApiClient.getUserById(id);
      const roles = Array.isArray(result.roles) ? result.roles : [];
      this.user = {
        ...result,
        roles: roles,
        jobTitle: this.jobTitles?.find((el) => el.id === result.jobTitleId)?.title || '',
      };
      return this.user;
    } catch (error) {
      console.error('Error in EmployeesStore.getUserById:', error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  editUser = async (user: Employee | any) => {
    this.isLoading = true;
    console.log(user);
    const result = await this.ApiStore.EmployeesApiClient.editUser(user).finally(() => {
      this.isLoading = false;
    });

    return result;
  }

  uploadPhoto = async (file: UploadFile, userId: string) => {
    this.isLoading = true;
    const photo = await this.ApiStore.EmployeesApiClient.photoUpload(file, userId).finally(() => {
      this.isLoading = false;
    });
    return photo;
  }

  getHistoryByUser = async (id: string): Promise<HistoryItem[]> => {
    this.isLoading = true;
    const user = await this.ApiStore.EmployeesApiClient.getHistoryByUser(id).then(result => {
      return result;
    }).finally(() => {
      this.isLoading = false;
    });
    return user;
  }

  deleteUser = async (userId: string): Promise<void> => {
    this.setLoading(true);
    try {
      await this.ApiStore.EmployeesApiClient.deleteUser(userId);
      this.users = this.users.filter(user => user.id !== userId);
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  };

  deleteUserPermanently = async (userId: string): Promise<void> => {
    this.setLoading(true);
    try {
      await this.ApiStore.EmployeesApiClient.deleteUserPermanently(userId);
      this.allUsers = this.allUsers.filter(user => user.id !== userId);
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  };
  //#endregion


  //#region Experience
  addExperience = async (company: string, project: string, projectId: string, position: string, description: string, startDate: any, endDate: any) => {
    return await this.ApiStore.EmployeesApiClient.addExperience(company, project, projectId, position, description, startDate, endDate);
  };

  editExperience = async (prevItem: Experience, company: string, project: string, projectId: string, position: string, description: string, startDate: any, endDate: any) => {
    return await this.ApiStore.EmployeesApiClient.editExperience(prevItem, company, project, projectId, position, description, startDate, endDate);
  };

  removeExperience = async (company: string, project: string, position: string, description: string, startDate: any, endDate: any) => {
    return await this.ApiStore.EmployeesApiClient.removeExperience(company, project, position, description, startDate, endDate).then(result => {
      return result;
    });
  }
  //#endregion


  //#region Password Recovery
  forgetPassword = async (email: string, language: string): Promise<void> => {
    this.setLoading(true);
    try {
      await this.ApiStore.EmployeesApiClient.forgetPassword(email, language);
    } catch (error) {
      console.error("Error forget password:", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  };

  restorePassword = async (email: string, token: string, newPassword: string): Promise<void> => {
    this.setLoading(true);
    try {
      await this.ApiStore.EmployeesApiClient.restorePassword(email, token, newPassword);
    } catch (error) {
      console.error("Error restore password:", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  };
  //#endregion
}
export default EmployeesStore;
